import { Component, OnDestroy, OnInit } from '@angular/core';
import { timer } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthenticationService, LoginState, TokenState } from '../services/authentication.service';
import { LogCategory, LogCoreComponent, LoggerService, LogType, LogCoreService, LogDirective, LogPage, LogService, LogWidget } from '../services/logger.service';
import { UtilityService } from '../services/utility.service';

@Component({
    selector: 'app-environment',
    templateUrl: './environment.component.html',
    styleUrls: ['./environment.component.scss'],
    standalone: false
})

export class EnvironmentComponent implements OnInit, OnDestroy {

    public expiresIn = 0;
    public refreshableFor = 0; 
    public app = 'Unknown';
    public database = 'Unknown';
    public userId = 'Unknown';
    public pid = 'Unknown';
    public maySpoof = 'Unknown';
    public spoofer = 'None';
    public audiences = 'None';
    public apiUrl = environment.apiUrl;
    public ssoUrl = environment.ssoUrl;
    public prod = environment.production;
    public nowOverride = null;
    public loginState = ''; 
    public tokenState = ''; 

    public logCategoryValues = [];

    public logCoreComponentValues = [];
    public logCoreServiceValues = [];
    public logDirectiveValues = [];
    public logPageValues = [];
    public logServiceValues = [];
    public logWidgetValues = [];

    constructor(
        private auth: AuthenticationService,
        private logger: LoggerService,
        public util: UtilityService,
    ) {
        this.log("constructor", null, LogCategory.Constructor);

        // Categories 
        let keys = Object.keys(LogCategory); 
        keys.slice(keys.length / 2).sort().forEach((val) => this.logCategoryValues.push({ name: val, value: LogCategory[val] }));

        // Vertical slices 
        keys = Object.keys(LogCoreComponent);
        keys.slice(keys.length / 2).sort().forEach((val) => this.logCoreComponentValues.push({ name: val, value: LogCoreComponent[val] }));

        keys = Object.keys(LogCoreService);
        keys.slice(keys.length / 2).sort().forEach((val) => this.logCoreServiceValues.push({ name: val, value: LogCoreService[val] }));

        keys = Object.keys(LogDirective);
        keys.slice(keys.length / 2).sort().forEach((val) => this.logDirectiveValues.push({ name: val, value: LogDirective[val] }));

        keys = Object.keys(LogPage);
        keys.slice(keys.length / 2).sort().forEach((val) => this.logPageValues.push({ name: val, value: LogPage[val] }));

        keys = Object.keys(LogService);
        keys.slice(keys.length / 2).sort().forEach((val) => this.logServiceValues.push({ name: val, value: LogService[val] }));

        keys = Object.keys(LogWidget);
        keys.slice(keys.length / 2).sort().forEach((val) => this.logWidgetValues.push({ name: val, value: LogWidget[val] }));
    }

    private log(method: string = null, message = null, category: LogCategory = LogCategory.Default): void {
        method = (method) ? '.' + method + '()' : '';
        message = (message) ? message.toString() : '';
        this.logger.try(this.constructor.name + method + ': ' + message, LogType.CoreComponent, LogCoreComponent.Environment, category);
    }

    public get show() {
        return environment.showDebug; 
    }

    public getValue(values : number[]): number {
        var retVal = 0;
        values.filter(a => !!a["checked"]).forEach(a => retVal += +a["value"]);
        return retVal;
    }

    public updateAll(type: string, value: boolean) {

        if (type == "Category") {
            this.logCategoryValues.forEach(a => a.checked = value);
            localStorage.setItem('logCategory', this.getValue(this.logCategoryValues).toString());
        }

        if (type == "CoreComponent") {
            this.logCoreComponentValues.forEach(a => a.checked = value);
            localStorage.setItem('logCoreComponent', this.getValue(this.logCoreComponentValues).toString());
        }
        if (type == "CoreService") {
            this.logCoreServiceValues.forEach(a => a.checked = value);
            localStorage.setItem('logCoreService', this.getValue(this.logCoreServiceValues).toString());
        }
        if (type == "Directive") {
            this.logDirectiveValues.forEach(a => a.checked = value);
            localStorage.setItem('logDirective', this.getValue(this.logDirectiveValues).toString());
        }
        if (type == "Page") {
            this.logPageValues.forEach(a => a.checked = value);
            localStorage.setItem('logPage', this.getValue(this.logPageValues).toString());
        }
        if (type == "Service") {
            this.logServiceValues.forEach(a => a.checked = value);
            localStorage.setItem('logService', this.getValue(this.logServiceValues).toString());
        }
        if (type == "Widget") {
            this.logWidgetValues.forEach(a => a.checked = value);
            localStorage.setItem('logWidget', this.getValue(this.logWidgetValues).toString());
        }
    }


    public updateSelection(item: any, type: string) {
        item.checked = !item.checked

        if (type == "Category")
            localStorage.setItem('logCategory', this.getValue(this.logCategoryValues).toString());

        if (type == "CoreComponent")
            localStorage.setItem('logCoreComponent', this.getValue(this.logCoreComponentValues).toString());
        if (type == "CoreService")
            localStorage.setItem('logCoreService', this.getValue(this.logCoreServiceValues).toString());
        if (type == "Directive")
            localStorage.setItem('logDirective', this.getValue(this.logDirectiveValues).toString());
        if (type == "Page")
            localStorage.setItem('logPage', this.getValue(this.logPageValues).toString());
        if (type == "Service")
            localStorage.setItem('logService', this.getValue(this.logServiceValues).toString());
        if (type == "Widget")
            localStorage.setItem('logWidget', this.getValue(this.logWidgetValues).toString());
    }

    ngOnInit() {

        this.log("ngOnInit", null, LogCategory.Init);

        // No need to unsubscribe for finite timer observable 
        timer(0, 1776).subscribe( () => {
            this.log("ngOnInit", "heartbeat received", LogCategory.SubscriptionData);
            var token = this.auth.token;
            if (token.isFound) {
                var payload = token.payload;
                this.app = payload.App.toString();
                this.database = payload.Database.toString();
                this.userId = payload.sub.toString();
                this.pid = payload.Pid.toString();
                this.audiences = payload['Audiences'];
                if (!!payload['Kfbs.Context.SpoofAs'])
                    this.maySpoof = payload['Kfbs.Context.SpoofAs'].toString();
                if (!!payload['ActiveSpoofer'])
                    this.spoofer = payload['ActiveSpoofer'].toString();
                this.loginState = LoginState[this.auth.loginState];
                this.tokenState = TokenState[this.auth.tokenStateSubject.value];

                this.nowOverride = localStorage.getItem('nowOverride');

                var now = new Date();
                var exp = new Date(0);
                exp.setUTCSeconds(payload.exp);

                var ref = new Date(0);
                ref.setUTCSeconds(payload.exp + payload.RefreshWindow * 60);

                this.expiresIn = Math.floor((exp.getTime() - now.getTime()) / 1000);
                this.refreshableFor = Math.floor((ref.getTime() - now.getTime()) / 1000);
            }
        })
    }

    ngOnDestroy() {

        this.log("ngOnInit", null, LogCategory.Destroy);

    }
}
